import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, LucideIcon } from 'lucide-react';
import * as Icons from 'lucide-react';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { FadeIn, Stagger, StaggerItem } from '@/components/motion/FadeIn';
import { MaskReveal } from '@/components/motion/Reveal';
import { useFeaturedServices } from '@/lib/cms';

interface Props {
  headline: string;
  view_all_label: string;
  view_all_href: string;
}

export function HomeServicesPreview({ headline, view_all_label, view_all_href }: Props) {
  const { data: services = [], isLoading } = useFeaturedServices();

  return (
    <MaskReveal className="section-y-lg" tilt={-1} duration={1.2}>
      <div className="container-wide">
        <FadeIn>
          <div className="flex items-end justify-between gap-6">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-500">What we do</p>
              <h2 className="mt-3 text-h2 text-fg">{headline}</h2>
            </div>
            <Link
              to={view_all_href}
              className="hidden items-center gap-1 text-sm text-gold-500 hover:underline md:inline-flex"
            >
              {view_all_label}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </FadeIn>

        <Stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {isLoading
            ? Array.from({ length: 6 }).map((_, i) => (
                <StaggerItem key={`s-${i}`}>
                  <Card className="h-full animate-pulse">
                    <CardHeader>
                      <div className="h-10 w-10 rounded-md bg-bg-subtle" />
                      <div className="mt-4 h-5 w-2/3 rounded bg-bg-subtle" />
                      <div className="mt-2 h-4 w-full rounded bg-bg-subtle" />
                    </CardHeader>
                  </Card>
                </StaggerItem>
              ))
            : services.map((service) => {
                const IconName = (service.icon ?? 'Code2') as keyof typeof Icons;
                const Icon = (Icons[IconName] ?? Icons.Code2) as LucideIcon;
                return (
                  <StaggerItem key={service.id}>
                    <Link to={`/services#${service.slug}`} className="group block h-full">
                      <Card className="h-full transition-all hover:-translate-y-1 hover:border-gold-500/40 hover:shadow-gold">
                        <CardHeader>
                          <div className="inline-flex h-10 w-10 items-center justify-center rounded-md border border-border bg-bg-subtle text-gold-500 transition-colors group-hover:border-gold-500/40 group-hover:bg-gold-500/5">
                            <Icon className="h-5 w-5" />
                          </div>
                          <CardTitle className="mt-4">{service.title}</CardTitle>
                          <CardDescription>{service.summary}</CardDescription>
                          <span className="mt-3 inline-flex items-center gap-1 text-sm text-gold-500">
                            Learn more
                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </span>
                        </CardHeader>
                      </Card>
                    </Link>
                  </StaggerItem>
                );
              })}
        </Stagger>
      </div>
    </MaskReveal>
  );
}